import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Heart, MapPin, ArrowUpRight, Search } from "lucide-react";

const CITIES = ["Phoenix", "Scottsdale", "Gilbert"];
const CATEGORIES = [
  "Beauty",
  "Wellness",
  "Fitness",
  "Health",
  "Restaurants & Café",
  "Bakery & Catering",
  "Floral",
  "Retail & Boutique",
  "Fashion & Apparel",
  "Home & Design",
  "Photography",
  "Events & Hospitality",
  "Childcare & Family",
  "Pet Services",
  "Education & Coaching",
  "Marketing",
  "Professional Services",
  "Real Estate",
  "Nonprofit",
  "Other",
];

const FAV_KEY = "lh-favorites";

export default function DashHerGuide() {
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [favs, setFavs] = useState(() => JSON.parse(localStorage.getItem(FAV_KEY) || "[]"));

  const search = async () => {
    const params = {};
    if (city) params.city = city;
    if (category) params.category = category;
    if (q) params.q = q;
    const { data } = await api.get("/businesses", { params });
    setResults(data);
  };

  useEffect(() => { search(); /* eslint-disable-next-line */ }, []);

  const toggleFav = (id) => {
    const next = favs.includes(id) ? favs.filter((x) => x !== id) : [...favs, id];
    setFavs(next);
    localStorage.setItem(FAV_KEY, JSON.stringify(next));
  };

  return (
    <div data-testid="dashboard-her-guide-page">
      <div className="overline text-[var(--lh-mauve)] mb-3">HER Guide Directory</div>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-[0.95]">Discover. Save. <em>Support.</em></h1>

      <div className="mt-10 grid md:grid-cols-12 gap-5 items-end">
        <div className="md:col-span-3">
          <div className="overline text-[var(--lh-muted)] mb-2">City</div>
          <select className="input-line" value={city} onChange={(e) => setCity(e.target.value)} data-testid="dash-guide-city">
            <option value="">All</option>
            {CITIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-3">
          <div className="overline text-[var(--lh-muted)] mb-2">Category</div>
          <select className="input-line" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="dash-guide-category">
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-4">
          <div className="overline text-[var(--lh-muted)] mb-2">Search</div>
          <input className="input-line" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or keyword" data-testid="dash-guide-search-input" />
        </div>
        <div className="md:col-span-2">
          <button onClick={search} className="btn-primary w-full justify-center" data-testid="dash-guide-search-button">Find <Search size={14} /></button>
        </div>
      </div>

      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {results.map((b) => {
          const isFav = favs.includes(b.business_id);
          return (
            <article key={b.business_id} className="editorial-card overflow-hidden relative" data-testid={`dash-business-${b.business_id}`}>
              <button
                onClick={() => toggleFav(b.business_id)}
                className={`absolute top-4 right-4 z-10 p-2 transition ${isFav ? "bg-[var(--lh-mauve)] text-white" : "bg-white text-[var(--lh-black)] border border-[var(--lh-border)]"}`}
                aria-label="Save"
                data-testid={`fav-toggle-${b.business_id}`}
              >
                <Heart size={14} fill={isFav ? "currentColor" : "none"} />
              </button>
              {b.photo_url && <div className="aspect-[4/3] overflow-hidden bg-[var(--lh-surface)]"><img src={b.photo_url} alt={b.name} loading="lazy" decoding="async" className="w-full h-full object-cover" /></div>}
              <div className="p-6">
                <div className="overline text-[var(--lh-mauve)] mb-2">{b.category}</div>
                <h3 className="font-serif-display text-2xl">{b.name}</h3>
                <div className="overline text-[var(--lh-muted)] mt-1 flex items-center gap-1.5"><MapPin size={12} /> {b.city}</div>
                <p className="text-sm font-light text-[var(--lh-text)] mt-3 line-clamp-3">{b.description}</p>
                <a href={b.website || "#"} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 overline text-[var(--lh-peach)] hover:text-[var(--lh-warm-ivory)]">Visit <ArrowUpRight size={12} /></a>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
